import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing media playback (audio and video)
 * @returns {Object} Media playback state and functions
 */
const useMediaPlayback = () => {
  const [isPlaying, setIsPlaying] = useState({});
  const [currentTimes, setCurrentTimes] = useState({});
  const [durations, setDurations] = useState({});
  const [isMuted, setIsMuted] = useState({});
  const [playbackErrors, setPlaybackErrors] = useState({});
  
  const audioRefs = useRef({});
  const videoRefs = useRef({});
  const progressRefs = useRef({});

  // Store animation frame IDs for cleanup
  const animationFrameIds = useRef({});

  // Toggle audio play/pause
  const toggleAudio = useCallback((id) => {
    const audioElement = audioRefs.current[id];
    
    if (!audioElement) return;
    
    if (isPlaying[id]) {
      audioElement.pause();
      setIsPlaying(prev => ({ ...prev, [id]: false }));
    } else {
      // Pause all other audio first
      Object.keys(audioRefs.current).forEach(key => {
        if (key !== id && audioRefs.current[key]) {
          audioRefs.current[key].pause();
          setIsPlaying(prev => ({ ...prev, [key]: false }));
        }
      });
      
      try {
        const playPromise = audioElement.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(prev => ({ ...prev, [id]: true }));
              // Clear any previous errors
              setPlaybackErrors(prev => ({ ...prev, [id]: null }));
            })
            .catch(error => {
              console.error("Audio playback error:", error);
              setIsPlaying(prev => ({ ...prev, [id]: false }));
              setPlaybackErrors(prev => ({ ...prev, [id]: error.message || 'Failed to play audio' }));
            });
        } else {
          setIsPlaying(prev => ({ ...prev, [id]: true }));
        }
      } catch (error) {
        console.error("Audio playback error:", error);
        setIsPlaying(prev => ({ ...prev, [id]: false }));
        setPlaybackErrors(prev => ({ ...prev, [id]: error.message || 'Failed to play audio' }));
      }
    }
  }, [isPlaying]);

  // Toggle video play/pause
  const toggleVideo = useCallback((id) => {
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;
    
    if (isPlaying[id]) {
      // Pause video
      videoElement.pause();
      setIsPlaying(prev => ({ ...prev, [id]: false }));
      
      // Cancel animation frame when paused
      if (animationFrameIds.current[id]) {
        cancelAnimationFrame(animationFrameIds.current[id]);
      }
    } else {
      // Pause all other videos first
      Object.keys(videoRefs.current).forEach(key => {
        if (key !== id && videoRefs.current[key]) {
          videoRefs.current[key].pause();
          setIsPlaying(prev => ({ ...prev, [key]: false }));
          
          // Cancel animation frames for other videos
          if (animationFrameIds.current[key]) {
            cancelAnimationFrame(animationFrameIds.current[key]);
          }
        }
      });
      
      videoElement.muted = isMuted[id] === true;
      if (!videoElement.muted) {
        videoElement.volume = videoElement.volume > 0 ? videoElement.volume : 1;
      }

      // Add error handling for video playback
      try {
        const playPromise = videoElement.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Video playback started successfully
              setIsPlaying(prev => ({ ...prev, [id]: true }));
              // Clear any previous errors
              setPlaybackErrors(prev => ({ ...prev, [id]: null }));
              
              // Start progress bar animation
              const updateProgressBar = () => {
                if (!videoElement || !progressRefs.current[id]) return;
                
                const percentage = (videoElement.currentTime / videoElement.duration) * 100;
                progressRefs.current[id].style.width = `${percentage}%`;
                
                if (!videoElement.paused && !videoElement.ended) {
                  animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
                }
              };
              
              // Cancel any existing animation frame before starting a new one
              if (animationFrameIds.current[id]) {
                cancelAnimationFrame(animationFrameIds.current[id]);
              }
              
              animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
            })
            .catch(error => {
              // Video playback failed - likely format issue
              console.error("Video playback error:", error);
              
              // Set as not playing to avoid UI confusion
              setIsPlaying(prev => ({ ...prev, [id]: false }));
              
              // Store the error
              setPlaybackErrors(prev => ({ ...prev, [id]: error.message || 'Failed to play video' }));
              
              // Show a user-friendly error message
              if (!playbackErrors[id]) {
                alert("This video format may not be supported in your browser. iPhone videos (MOV/HEIC) often require conversion to be played on desktop browsers.");
              }
            });
        } else {
          // Older browsers might not return a promise
          setIsPlaying(prev => ({ ...prev, [id]: true }));
          
          // Start progress bar animation for older browsers
          const updateProgressBar = () => {
            if (!videoElement || !progressRefs.current[id]) return;
            
            const percentage = (videoElement.currentTime / videoElement.duration) * 100;
            progressRefs.current[id].style.width = `${percentage}%`;
            
            if (!videoElement.paused && !videoElement.ended) {
              animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
            }
          };
          
          // Start animation
          animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
        }
      } catch (error) {
        console.error("Video playback error:", error);
        setIsPlaying(prev => ({ ...prev, [id]: false }));
        setPlaybackErrors(prev => ({ ...prev, [id]: error.message || 'Failed to play video' }));
      }
    }
  }, [isPlaying, playbackErrors, isMuted]);

  // Toggle mute state for audio/video
  const toggleMute = useCallback((id, type = 'audio') => {
    const element = type === 'audio' ? audioRefs.current[id] : videoRefs.current[id];
    if (!element) return;
    
    // Update muted state
    element.muted = !element.muted;
    
    // Ensure the state is properly synced
    setIsMuted(prev => {
      const newState = { ...prev, [id]: element.muted };
      
      // For iOS devices, we need to ensure the video element's muted state is properly set
      if (type === 'video' && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
        // Double-check the video element's muted state
        if (videoRefs.current[id]) {
          videoRefs.current[id].muted = element.muted;
        }
      }
      
      return newState;
    });
  }, []);

  // Handle time update for audio/video
  const handleTimeUpdate = useCallback((id, element) => {
    if (!element) return;
    
    // Cancel any existing animation frame
    if (animationFrameIds.current[id]) {
      cancelAnimationFrame(animationFrameIds.current[id]);
    }
    
    // Update current time
    setCurrentTimes(prev => ({ ...prev, [id]: element.currentTime }));
    
    // Use requestAnimationFrame for smoother updates
    const updateProgressBar = () => {
      if (!element || !progressRefs.current[id]) return;
      
      // Calculate percentage with high precision
      const percentage = (element.currentTime / element.duration) * 100;
      
      // Apply the width update
      progressRefs.current[id].style.width = `${percentage}%`;
      
      // Continue animation if playing
      if (!element.paused && !element.ended) {
        animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
      }
    };
    
    // Start the animation frame loop
    animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
  }, []);

  // Handle media ended
  const handleMediaEnded = useCallback((id) => {
    // Cancel any existing animation frame
    if (animationFrameIds.current[id]) {
      cancelAnimationFrame(animationFrameIds.current[id]);
      delete animationFrameIds.current[id];
    }
    
    setIsPlaying(prev => ({ ...prev, [id]: false }));
    
    // Reset progress bar to 0
    if (progressRefs.current[id]) {
      progressRefs.current[id].style.width = '0%';
    }
    
    // Reset current time to 0
    setCurrentTimes(prev => ({ ...prev, [id]: 0 }));
  }, []);

  // Handle seeking in audio/video
  const handleSeek = useCallback((e, id, type = 'audio') => {
    e.stopPropagation();
    
    // Get the progress container
    const progressContainer = e.currentTarget;
    if (!progressContainer) return;
    
    // Calculate the new time based on click position
    const rect = progressContainer.getBoundingClientRect();
    // Use clientX for mouse events and touches[0].clientX for touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clickPosition = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    
    // Get the media element
    const element = type === 'audio' ? audioRefs.current[id] : videoRefs.current[id];
    if (!element || !element.duration) return;
    
    // Cancel any existing animation frame
    if (animationFrameIds.current[id]) {
      cancelAnimationFrame(animationFrameIds.current[id]);
    }
    
    // Set the new time
    const newTime = clickPosition * element.duration;
    element.currentTime = newTime;
    
    // Update current time state
    setCurrentTimes(prev => ({ ...prev, [id]: newTime }));
    
    // Update progress bar immediately
    if (progressRefs.current[id]) {
      const percentage = (newTime / element.duration) * 100;
      progressRefs.current[id].style.width = `${percentage}%`;
    }
    
    // If not playing, manually update the progress bar once
    if (element.paused || element.ended) return;
    
    // Restart animation frame if playing
    const updateProgressBar = () => {
      if (!element || !progressRefs.current[id]) return;
      
      const percentage = (element.currentTime / element.duration) * 100;
      progressRefs.current[id].style.width = `${percentage}%`;
      
      if (!element.paused && !element.ended) {
        animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
      }
    };
    
    animationFrameIds.current[id] = requestAnimationFrame(updateProgressBar);
  }, []);

  // Handle metadata loaded for audio/video
  const handleMetadataLoaded = useCallback((id, element) => {
    if (!element) return;
    setDurations(prev => ({ ...prev, [id]: element.duration }));
  }, []);

  // Clear playback error for a video
  const clearPlaybackError = useCallback((id) => {
    setPlaybackErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  }, []);
  
  // Reset video state when navigating in lightbox
  const resetVideoState = useCallback((id) => {
    if (videoRefs.current[id]) {
      videoRefs.current[id].pause();
      videoRefs.current[id].currentTime = 0;
    }
    
    // Reset states
    setIsPlaying(prev => ({ ...prev, [id]: false }));
    setCurrentTimes(prev => ({ ...prev, [id]: 0 }));
    
    // Reset progress bar
    if (progressRefs.current[id]) {
      progressRefs.current[id].style.width = '0%';
    }
    
    // Cancel any animation frames
    if (animationFrameIds.current[id]) {
      cancelAnimationFrame(animationFrameIds.current[id]);
      delete animationFrameIds.current[id];
    }
  }, []);

  // Clean up animation frames when component unmounts
  useEffect(() => {
    return () => {
      // Cancel all animation frames
      Object.values(animationFrameIds.current).forEach(id => {
        cancelAnimationFrame(id);
      });
    };
  }, []);

  return {
    isPlaying,
    currentTimes,
    durations,
    isMuted,
    playbackErrors,
    audioRefs,
    videoRefs,
    progressRefs,
    toggleAudio,
    toggleVideo,
    toggleMute,
    handleTimeUpdate,
    handleMediaEnded,
    handleSeek,
    handleMetadataLoaded,
    clearPlaybackError,
    resetVideoState
  };
};

export default useMediaPlayback; 